import { Flex, formatAddress, ItemSide } from "@opensea/ui-kit"
import { ArrowBack, Check } from "@opensea/ui-kit/icons"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import {
  useAccount,
  useConnect,
  useConnectors,
  useGetPreviouslyConnectedAccountsForConnector,
} from "../../../../../polychain/hooks"
import { FlowActions, FlowState } from "../../../../../stores/flowStore"
import type { Connector } from "../../../../../types"
import type { ViewWalletFlowState } from "../../../../flow-state"
import { useConnectFlow } from "../../../../hooks/useConnectFlow"
import {
  ProfileDropdownItem,
  ProfileDropdownItemAvatar,
  ProfileDropdownItemContent,
  ProfileDropdownItemDescription,
  ProfileDropdownItemTitle,
} from "./ProfileDropdownItem"

type SwitchWalletContentProps = {
  onCancel: () => void
}

export function SwitchWalletContent({ onCancel }: SwitchWalletContentProps) {
  const t = useTranslations("wallet.SwitchWalletContent")
  const account = useAccount()
  const { activeConnector, connectors } = useConnectors()

  return (
    <>
      <ProfileDropdownItem
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.preventDefault()
          onCancel()
        }}
      >
        <ProfileDropdownItemAvatar
          className="bg-transparent"
          icon={ArrowBack}
        />
        <ProfileDropdownItemContent>
          <ProfileDropdownItemTitle>{t("back")}</ProfileDropdownItemTitle>
        </ProfileDropdownItemContent>
      </ProfileDropdownItem>
      {activeConnector && (
        <WalletOption connector={activeConnector} isConnected />
      )}
      {connectors
        .filter(connector => connector.id !== account.connectorId)
        .map(connector => (
          <WalletOption connector={connector} key={connector.id} />
        ))}
    </>
  )
}

type WalletOptionProps = {
  connector: Connector
  isConnected?: boolean
}

export function WalletOption({ connector, isConnected }: WalletOptionProps) {
  const { connect } = useConnect()
  const connectFlow = useConnectFlow()
  const getPreviouslyConnectedAccounts =
    useGetPreviouslyConnectedAccountsForConnector()
  const { flow } = useSnapshot(FlowState)

  return (
    <ProfileDropdownItem
      disabled={isConnected}
      onClick={async () => {
        if (connector.type === "embedded" && !connector.isConnected()) {
          connectFlow()
        } else {
          const address = await connect(connector)
          if (flow && flow.type === "view-wallet") {
            FlowActions.setFlowField<ViewWalletFlowState>("address", address)
          }
        }
      }}
    >
      {connector.icon && <ProfileDropdownItemAvatar src={connector.icon} />}
      <ProfileDropdownItemContent>
        <ProfileDropdownItemTitle>{connector.name}</ProfileDropdownItemTitle>
        <ProfileDropdownItemDescription className="text-start">
          <Flex className="flex-wrap justify-between gap-1 text-2xs">
            {getPreviouslyConnectedAccounts(connector.id)
              .map(previouslyConnectedAccount =>
                formatAddress(previouslyConnectedAccount.address),
              )
              .map(formattedAddress => (
                <div key={formattedAddress}>{formattedAddress}</div>
              ))}
          </Flex>
        </ProfileDropdownItemDescription>
      </ProfileDropdownItemContent>
      {isConnected && (
        <ItemSide>
          <Check />
        </ItemSide>
      )}
    </ProfileDropdownItem>
  )
}
